import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdBottomBanner } from '../components/AdSense';

const ScientificCalculatorPage = () => {
  const [display, setDisplay] = useState('0');
  const [memory, setMemory] = useState(0);
  const [lastAnswer, setLastAnswer] = useState(0);
  const [isDegree, setIsDegree] = useState(true);
  const [expression, setExpression] = useState('');
  const displayRef = useRef(null);

  const buttons = [
    ['sin', 'cos', 'tan', 'Deg', 'Rad'],
    ['sin⁻¹', 'cos⁻¹', 'tan⁻¹', 'π', 'e'],
    ['x^y', 'x³', 'x²', 'e^x', '10^x'],
    ['ʸ√x', '³√x', '√x', 'ln', 'log'],
    ['(', ')', '1/x', '%', 'n!'],
    ['7', '8', '9', '+', 'Back'],
    ['4', '5', '6', '-', 'Ans'],
    ['1', '2', '3', '×', 'M+'],
    ['0', '.', 'EXP', '÷', 'M-'],
    ['±', 'RND', 'AC', '=', 'MR']
  ];

  const factorial = (n) => {
    if (n < 0) return NaN;
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) result *= i;
    return result;
  };

  const toRadians = (deg) => deg * (Math.PI / 180);
  const toDegrees = (rad) => rad * (180 / Math.PI);

  const handleButton = (btn) => {
    switch (btn) {
      case 'AC':
        setDisplay('0');
        setExpression('');
        break;
      case 'Back':
        setDisplay(prev => prev.length > 1 ? prev.slice(0, -1) : '0');
        break;
      case '=':
        try {
          let expr = display
            .replace(/×/g, '*')
            .replace(/÷/g, '/')
            .replace(/π/g, Math.PI.toString())
            .replace(/e(?![x])/g, Math.E.toString());
          const result = Function('"use strict"; return (' + expr + ')')();
          setLastAnswer(result);
          setDisplay(String(result));
          setExpression(display + ' =');
        } catch {
          setDisplay('Error');
        }
        break;
      case 'sin':
      case 'cos':
      case 'tan':
        try {
          const val = parseFloat(display);
          const angle = isDegree ? toRadians(val) : val;
          const result = Math[btn](angle);
          setDisplay(String(result));
        } catch { setDisplay('Error'); }
        break;
      case 'sin⁻¹':
      case 'cos⁻¹':
      case 'tan⁻¹':
        try {
          const val = parseFloat(display);
          const func = btn === 'sin⁻¹' ? 'asin' : btn === 'cos⁻¹' ? 'acos' : 'atan';
          let result = Math[func](val);
          if (isDegree) result = toDegrees(result);
          setDisplay(String(result));
        } catch { setDisplay('Error'); }
        break;
      case '√x':
        setDisplay(prev => String(Math.sqrt(parseFloat(prev))));
        break;
      case '³√x':
        setDisplay(prev => String(Math.cbrt(parseFloat(prev))));
        break;
      case 'x²':
        setDisplay(prev => String(Math.pow(parseFloat(prev), 2)));
        break;
      case 'x³':
        setDisplay(prev => String(Math.pow(parseFloat(prev), 3)));
        break;
      case 'x^y':
        setDisplay(prev => prev + '^');
        break;
      case 'ʸ√x':
        setDisplay(prev => prev + '^(1/');
        break;
      case 'e^x':
        setDisplay(prev => String(Math.exp(parseFloat(prev))));
        break;
      case '10^x':
        setDisplay(prev => String(Math.pow(10, parseFloat(prev))));
        break;
      case 'ln':
        setDisplay(prev => String(Math.log(parseFloat(prev))));
        break;
      case 'log':
        setDisplay(prev => String(Math.log10(parseFloat(prev))));
        break;
      case '1/x':
        setDisplay(prev => String(1 / parseFloat(prev)));
        break;
      case 'n!':
        setDisplay(prev => String(factorial(parseInt(prev))));
        break;
      case '%':
        setDisplay(prev => String(parseFloat(prev) / 100));
        break;
      case '±':
        setDisplay(prev => prev.startsWith('-') ? prev.slice(1) : '-' + prev);
        break;
      case 'RND':
        setDisplay(String(Math.random()));
        break;
      case 'π':
        setDisplay(String(Math.PI));
        break;
      case 'e':
        setDisplay(String(Math.E));
        break;
      case 'Deg':
        setIsDegree(true);
        break;
      case 'Rad':
        setIsDegree(false);
        break;
      case 'Ans':
        setDisplay(prev => prev === '0' ? String(lastAnswer) : prev + lastAnswer);
        break;
      case 'M+':
        setMemory(prev => prev + parseFloat(display));
        break;
      case 'M-':
        setMemory(prev => prev - parseFloat(display));
        break;
      case 'MR':
        setDisplay(String(memory));
        break;
      case 'EXP':
        setDisplay(prev => prev + 'e');
        break;
      default:
        if (/[0-9.]/.test(btn)) {
          setDisplay(prev => prev === '0' && btn !== '.' ? btn : prev + btn);
        } else if (['+', '-', '×', '÷', '(', ')'].includes(btn)) {
          setDisplay(prev => prev + btn);
        }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;
      if (/[0-9.]/.test(key)) handleButton(key);
      else if (key === '+') handleButton('+');
      else if (key === '-') handleButton('-');
      else if (key === '*') handleButton('×');
      else if (key === '/') handleButton('÷');
      else if (key === 'Enter' || key === '=') handleButton('=');
      else if (key === 'Backspace') handleButton('Back');
      else if (key === 'Escape') handleButton('AC');
      else if (key === '(' || key === ')') handleButton(key);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display, isDegree, lastAnswer]);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/math" className="text-cyan-400 hover:text-cyan-300">Math</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Scientific Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-6">Scientific Calculator</h1>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4">
            <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg p-4 mb-4">
              <p className="text-gray-500 text-xs text-right min-h-[16px]">{expression}</p>
              <p ref={displayRef} className="text-right text-3xl font-mono text-white break-all">{display}</p>
              <div className="flex justify-between items-center mt-2">
                <span className={`text-xs px-2 py-0.5 rounded ${isDegree ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-700 text-gray-400'}`}>DEG</span>
                <span className={`text-xs px-2 py-0.5 rounded ${!isDegree ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-700 text-gray-400'}`}>RAD</span>
                <span className="text-xs text-gray-500">M: {memory}</span>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-1">
              {buttons.flat().map((btn, i) => {
                const isNumber = /^[0-9.]$/.test(btn);
                const isOperator = ['+', '-', '×', '÷', '='].includes(btn);
                const isFunction = ['sin', 'cos', 'tan', 'sin⁻¹', 'cos⁻¹', 'tan⁻¹', 'ln', 'log', '√x', '³√x', 'ʸ√x', 'x²', 'x³', 'x^y', 'e^x', '10^x', '1/x', 'n!', '%'].includes(btn);
                const isMemory = ['M+', 'M-', 'MR', 'Ans'].includes(btn);
                const isControl = ['AC', 'Back', 'Deg', 'Rad', 'EXP', '±', 'RND'].includes(btn);
                const isConstant = ['π', 'e'].includes(btn);
                
                let bgColor = 'bg-[#2a2a2a] hover:bg-[#3a3a3a]';
                if (isNumber) bgColor = 'bg-[#3a3a3a] hover:bg-[#4a4a4a]';
                if (isOperator) bgColor = 'bg-cyan-600 hover:bg-cyan-500';
                if (btn === '=') bgColor = 'bg-green-600 hover:bg-green-500';
                if (btn === 'AC') bgColor = 'bg-red-600 hover:bg-red-500';
                if ((btn === 'Deg' && isDegree) || (btn === 'Rad' && !isDegree)) bgColor = 'bg-cyan-600';

                return (
                  <button
                    key={i}
                    onClick={() => handleButton(btn)}
                    className={`${bgColor} text-white font-medium py-3 px-2 rounded-lg text-sm transition-colors`}
                  >
                    {btn}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-gray-500 text-sm mt-4 text-center">
            This is an online scientific calculator. You can click the buttons or type to perform calculations.
          </p>

          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/fraction-calculator" className="text-cyan-400 hover:text-cyan-300">Fraction Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/percentage-calculator" className="text-cyan-400 hover:text-cyan-300">Percentage Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/standard-deviation-calculator" className="text-cyan-400 hover:text-cyan-300">Standard Deviation</Link>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Math Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Scientific', path: '/scientific-calculator' },
                { name: 'Fraction', path: '/fraction-calculator' },
                { name: 'Percentage', path: '/percentage-calculator' },
                { name: 'Random Number', path: '/random-number-generator' },
                { name: 'Triangle', path: '/triangle-calculator' },
                { name: 'Standard Deviation', path: '/standard-deviation-calculator' },
              ].map((calc) => (
                <Link key={calc.path} to={calc.path}
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors">
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>

          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ScientificCalculatorPage;
